import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { catchError, forkJoin, from, mergeMap, Observable, of, switchMap } from 'rxjs';
import { UserService } from 'auth-data-access';
import { CmsService, NewLandingPageDocumentDataRedirectsItem } from 'prismic';
import { RedirectService } from 'redirect';
import { isLoginPath } from 'utils';

interface UrlPrefix {
  [key: string]: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
/**
 * If there is no language parameter it redirects to the current language versionlanguage version
 */
export class LandingPageRedirectGuard implements CanActivate {
  private router = inject(Router);
  private cms = inject(CmsService);
  private user = inject(UserService);
  private redirectService = inject(RedirectService);
  private platformId = inject(PLATFORM_ID);
  private homeUID = 'home';

  private calcUidAndPageType(route: ActivatedRouteSnapshot) {
    let uid: string | undefined = route.url.map(u => u.path).join('--') || this.homeUID;

    // Handle different types of prismic pages
    let prismicPageType = 'new_landing_page';
    const urlPrefixToPageTypes: UrlPrefix = {
      product: 'product_page',
    };

    for (const urlPrefix in urlPrefixToPageTypes) {
      if (route.url.length > 0 && urlPrefix === route.url[0].path) {
        prismicPageType = urlPrefixToPageTypes[urlPrefix];
        uid = uid?.replace(urlPrefix, '');
        break;
      }
    }

    if (uid) {
      if (uid.length > 1 && uid.startsWith('--')) {
        // remove any `--` at the beginning
        uid = uid.substring(2);
      }
    }
    return { uid, prismicPageType };
  }

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const { uid, prismicPageType } = this.calcUidAndPageType(route);
    if (uid?.startsWith('404')) return this.handleNotFound(route);

    return forkJoin([from(this.cms.getByUid(prismicPageType, uid)), from(this.cms.getByUid('redirect', uid))]).pipe(
      mergeMap(([landingPageResp, redirect]) => {
        if (redirect) {
          const url = redirect?.data.redirect_to as string;
          if (url) return this.redirectService.redirectTo(url, route.queryParams);
        }

        // Setting the response into the route data so that the resolver can use it
        route.data = { ...route.data, landingPageData: landingPageResp };
        Object.preventExtensions(route.data);

        // If the landing page data was not found then we handle it here
        if (!landingPageResp) {
          if (isLoginPath(route.url)) {
            return this.redirectService.redirectTo('/auth/login', route.queryParams);
          }
          return this.handleNotFound(route);
        }

        const redirects: NewLandingPageDocumentDataRedirectsItem[] = (
          landingPageResp.data.redirects as NewLandingPageDocumentDataRedirectsItem[]
        ).filter((redirect: NewLandingPageDocumentDataRedirectsItem) => redirect.redirect_url !== null);

        if (!redirects?.length) return of(true);

        const loggedInRedirect = redirects.findLast(redirect => redirect.logged_in_status === 'Logged in');
        const notLoggedInRedirect = redirects.findLast(redirect => redirect.logged_in_status === 'Not logged in');
        const anyUsersRedirect = redirects.findLast(redirect => redirect.logged_in_status === 'Any');

        // Early redirection if the auth status is irrelevant and we have a redirection for everyone set, no need to wait for hydration and authentication check
        if (!loggedInRedirect && !notLoggedInRedirect && anyUsersRedirect) {
          return this.redirectService.redirectTo(anyUsersRedirect.redirect_url, route.queryParams);
        }

        // Authentication is not handled server side, so we wait for hydration before applying this logic
        if (isPlatformBrowser(this.platformId)) {
          return this.user.isAuthenticated().pipe(
            switchMap(isAuthenticated => {
              if (isAuthenticated && loggedInRedirect) {
                return this.redirectService.redirectTo(loggedInRedirect.redirect_url, route.queryParams);
              }

              if (!isAuthenticated && notLoggedInRedirect) {
                return this.redirectService.redirectTo(notLoggedInRedirect.redirect_url, route.queryParams);
              }

              if (anyUsersRedirect) {
                return this.redirectService.redirectTo(anyUsersRedirect.redirect_url, route.queryParams);
              }

              return of(true);
            })
          );
        }

        return of(true);
      }),
      catchError(error => {
        console.error(error);
        return of(false);
      })
    );
  }

  public handleNotFound(route: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    const fullPath = route.url.map(u => u.path).join('/');
    // Backup for the prismic not found page not being present
    if (fullPath === 'not-found') {
      return of(
        this.router.createUrlTree(['404'], {
          // queryParams: { url: route.url.toString(), urlParams: JSON.stringify(route.queryParams) },
          queryParamsHandling: 'merge',
        })
      );
    }
    return this.redirectService.redirectTo('/not-found');
  }
}
